import { useContext, useEffect, useRef, useState } from "react";
import "./CheckOutPayment.css";
import { CardElement, ExpressCheckoutElement } from "@stripe/react-stripe-js";
import CheckOutList from "../CheckOutList/CheckOutList";
import { ReactComponent as Link } from "../../assets/icons/link.svg";
import ButtonPlane from "../../UI/Button/ButtonPlane";
import Button from "../../UI/Button/Button";
import { connect } from "react-redux";
import { setPageCount } from "../../state_management/actions";
import { stateType } from "../../store";
import { handleCopy } from "../../utilities/copyHandler";
import { baseName } from "../../settings";
import { useAsync } from "../../utilities/asyncFunc";
import { reserve } from "../../state_management/epics/order";
import { usePayType } from "./paymentHook";
import CheckOutPaymentLeft from "./CheckOutPaymentLeft";
import { proceed_val_type } from "../../state_management/reducers/order";
import { LocaleContext } from "../../AppProvider";

const CheckOutPayment = ({
  clientSecret,
  orderId,
  orderSuccess,
  Total,
  depositUnavailable,
  usePay,
  error,
  processing,
  disabled
}: {
  clientSecret: string | undefined;
  orderId: number | undefined;
  orderSuccess: boolean;
  Total: number;
  depositUnavailable: boolean;
  usePay: usePayType;
  error: string | undefined;
  processing: boolean;
  disabled: boolean;
}) => {
  const {
    paymentTypeHandler,
    full_payment,
    createOrUpdateOrderRes,
    depositValue,
    TotalDeposit,
    handleSubmit,
    handleExpressCheckoutElementOnConfirm,
    handleChange,
    showContent,
    stripe,
    elements
  } = usePay;

  const token = createOrUpdateOrderRes?.data?.order.reschedule_token;
  const loading = createOrUpdateOrderRes.loading;
  const card = elements?.getElement(CardElement) || null;
  const disabledButton =
    !card ||
    loading ||
    disabled ||
    processing ||
    orderSuccess ||
    !stripe ||
    !clientSecret ||
    !elements ||
    depositValue === undefined;

  return (
    <div className="checkOutPayment_wrapper">
      <form
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();
          if (disabledButton) {
            return;
          } else {
            handleSubmit(e, CardElement);
          }
        }}
        className="checkout_form checkout_content_wrapper"
      >
        <div className="checkout_left">
          <div>
            <CheckOutPaymentLeft
              depositUnavailable={depositUnavailable}
              full_payment={full_payment}
              Total={Total}
              paymentTypeHandler={paymentTypeHandler}
              TotalDeposit={TotalDeposit}
              children={
                showContent() ? (
                  <>
                    <CardElement
                      id="checkout_card_field_id"
                      onChange={handleChange}
                    />
                    {error && <div id="payment-message">{error}</div>}{" "}
                    <ExpressCheckoutElement
                      options={{
                        layout: {
                          maxColumns: 3
                        }
                      }}
                      className="express_checkout"
                      onConfirm={handleExpressCheckoutElementOnConfirm}
                    />
                  </>
                ) : (
                  ``
                )
              }
            />
            <PaymentButtons
              orderId={orderId}
              disabled={disabledButton}
              link={
                token && orderId
                  ? `${window.location.origin}${baseName}/checkout?direct=true&id=${orderId}&token=${token}`
                  : undefined
              }
              smallScreen
            />
          </div>
        </div>
        <div className="checkout_right">
          <CheckOutList
            buttonJSX={
              <PaymentButtons
                orderId={orderId}
                disabled={disabledButton}
                link={
                  token && orderId
                    ? `${window.location.origin}${baseName}/checkout?direct=true&id=${orderId}&token=${token}`
                    : undefined
                }
              />
            }
          />
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  pageCount: state.commonReducer.pageCount,
  orderDirect: state.orderReducer.direct,
  orderSuccess: state.orderReducer.success
});

const mapDispatchToProps = (dispatch: any) => ({
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckOutPayment);

const PaymentButtons = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  ({
    pageCount,
    setPageCount,
    smallScreen,
    link,
    disabled,
    orderSuccess,
    orderDirect,
    orderId
  }: {
    pageCount: proceed_val_type;
    setPageCount: (data: number) => void;
    smallScreen?: boolean;
    disabled?: boolean;
    link: string | undefined;
    orderSuccess: boolean;
    orderDirect: boolean;
    orderId?: number;
  }) => {
    const { t } = useContext(LocaleContext);
    const deactivateBars = orderSuccess || orderDirect;
    const mountedRef = useRef(true);
    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const [timeoutId, setTimeoutId] = useState<number>();
    const [copied, setCopied] = useState(false);

    const reserveBooking = useAsync(
      {
        asyncFunc: reserve,
        funcParams: { id: orderId },
        immediate: false
      },
      []
    );

    return (
      <div className={`${smallScreen ? `showSm` : ``}`}>
        <div
          className={`check_list_btn_wrapper ${deactivateBars ? `direct` : ``}`}
        >
          {deactivateBars ? (
            ``
          ) : (
            <div className="check_list_btn_inner flex">
              <ButtonPlane
                onClick={() => setPageCount(pageCount - 1)}
                size="sm"
                span
              >
                {t("Back")}
              </ButtonPlane>
            </div>
          )}
          <div className="check_list_btn_inner flex">
            <Button disabled={disabled} type="submit" size="sm" span>
              {t("Pay")}
            </Button>
          </div>
        </div>
        {deactivateBars || !link ? (
          ``
        ) : (
          <div
            onClick={() => {
              handleCopy(`${link}`, () => {
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
                reserveBooking.execute({ id: orderId });
                setCopied(true);
                const copyTimeout = window.setTimeout(() => {
                  if (mountedRef.current) {
                    setCopied(false);
                  }
                }, 3000);
                setTimeoutId(copyTimeout);
              });
            }}
            className="copy_link_wrapper"
          >
            <div className="copy_link_icon">
              <Link className="img_div_contain" />
            </div>
            <div className="copy_link_container flex1">
              <span className="fontBlue">
                {t("Copy my cart link for someone else to pay")}
              </span>
              <div className={`copyNotif ${!copied ? `hideNotif` : ``}`}>
                {t("Copied")}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
